import { useState, useEffect, type CSSProperties } from 'react'
import { useCachedQuery } from '@/hooks/useCachedQuery'
import { useApi } from '@/hooks/useApi'
import StatCard from '@/components/shared/StatCard'
import type { AppCatalogo } from '@/lib/types'
import { ESTADO_META, cobroResumen } from './appsShared'
import { IconUsers, IconZap, IconWallet, IconTrendingUp, IconAlertCircle, IconExternalLink } from '@/components/icons/Icons'

/* Consumo real de la plataforma: el ERP sabe qué clientes tienen apps y su
   cuenta; el gateway tiene el saldo y el uso. Lo junta GET /api/apps/consumo. */
interface FilaConsumo {
  cliente_id: number
  cliente_nombre: string | null
  account_id: string | null
  balance: number | null
  unidad?: string
  saldo_bajo: boolean
  umbral_aviso: number | null
  consumo_mes: number | null
}

const num = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('es-PE', { maximumFractionDigits: 2 })

export default function AppsDashboard() {
  const { data: apps = [], isLoading } = useCachedQuery<AppCatalogo>('apps_catalogo')
  const api = useApi()
  const [consumo, setConsumo] = useState<{ disponible: boolean; periodo?: string; items: FilaConsumo[] } | null>(null)

  useEffect(() => {
    api.get<{ disponible: boolean; periodo?: string; items: FilaConsumo[] }>('/api/apps/consumo')
      .then(r => setConsumo(r ?? { disponible: false, items: [] }))
      .catch(() => setConsumo({ disponible: false, items: [] }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filas = consumo?.items ?? []
  const conCuenta = filas.filter(f => f.account_id)
  const saldoTotal = conCuenta.reduce((s, f) => s + (f.balance ?? 0), 0)
  const consumoMes = conCuenta.reduce((s, f) => s + (f.consumo_mes ?? 0), 0)
  const enRiesgo = conCuenta.filter(f => f.saldo_bajo).length

  return (
    <>
      {consumo && !consumo.disponible && (
        <div style={noticeStyle}>
          <IconAlertCircle size={16} style={{ color: 'var(--warning)', flexShrink: 0 }} />
          <span>No se pudo leer el consumo del gateway. Revisá <code style={{ opacity: 0.85 }}>GATEWAY_API_URL</code> y <code style={{ opacity: 0.85 }}>GATEWAY_API_KEY</code>.</span>
        </div>
      )}

      {enRiesgo > 0 && (
        <div style={noticeStyle}>
          <IconAlertCircle size={16} style={{ color: 'var(--warning)', flexShrink: 0 }} />
          <span>
            <strong>{enRiesgo}</strong> cliente{enRiesgo === 1 ? '' : 's'} por debajo de su umbral de aviso.
            Al llegar a cero, Gemini y RAGLink dejan de responder.
          </span>
        </div>
      )}

      {/* KPIs reales del gateway */}
      <div style={kpiGrid}>
        <StatCard icon={<IconUsers size={18} />} label="Clientes con apps" color="primary"
          value={String(conCuenta.length)} trend={`${filas.length} activaciones`} />
        <StatCard icon={<IconZap size={18} />} label={`Créditos consumidos (${consumo?.periodo ?? 'mes'})`} color="info"
          value={num(consumoMes)} trend="Suma de todas las cuentas" />
        <StatCard icon={<IconWallet size={18} />} label="Saldo total en cuentas" color="success"
          value={num(saldoTotal)} trend="Créditos sin consumir" />
        <StatCard icon={<IconTrendingUp size={18} />} label="Clientes en riesgo" color="warning"
          value={String(enRiesgo)} trend="Bajo su umbral de aviso" />
      </div>

      {/* Consumo por cliente */}
      <h3 style={sectionTitle}>Consumo por cliente</h3>
      {!consumo ? (
        <div style={{ color: 'var(--text-muted)', padding: '20px 0' }}>Cargando consumo…</div>
      ) : conCuenta.length === 0 ? (
        <div style={emptyStyle}>Ningún cliente tiene todavía una cuenta con consumo.</div>
      ) : (
        <div className="table-container" style={{ marginBottom: 28 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Cliente</th><th>Cuenta</th><th>Saldo</th>
                <th>Consumo del mes</th><th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {conCuenta.map(f => (
                <tr key={f.cliente_id}>
                  <td><strong>{f.cliente_nombre || `#${f.cliente_id}`}</strong></td>
                  <td><code>{f.account_id}</code></td>
                  <td>{num(f.balance)} {f.unidad ?? 'creditos'}</td>
                  <td>{num(f.consumo_mes)}</td>
                  <td>
                    <span className={`badge ${f.saldo_bajo ? 'badge-pausado' : 'badge-activo'}`}>
                      {f.saldo_bajo ? `Bajo${f.umbral_aviso != null ? ` (< ${f.umbral_aviso})` : ''}` : 'OK'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Tarjeta de consumo por app */}
      <h3 style={sectionTitle}>Consumo por app</h3>
      {isLoading ? (
        <div style={{ color: 'var(--text-muted)', padding: '20px 0' }}>Cargando apps…</div>
      ) : apps.length === 0 ? (
        <div style={emptyStyle}>Sin apps en el catálogo aún.</div>
      ) : (
        <div style={cardGrid}>
          {apps.map(app => {
            const meta = ESTADO_META[app.estado] || ESTADO_META.produccion
            return (
              <div key={app.id} style={appCard}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{app.nombre}</span>
                  <span className="badge" style={{ background: meta.bg, color: meta.color, fontSize: '0.66rem', fontWeight: 700 }}>{meta.label}</span>
                </div>
                {app.descripcion && <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '6px 0 0', lineHeight: 1.5 }}>{app.descripcion}</p>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
                  <div style={metricRow}><span style={metricLabel}>Cobro</span><span style={metricVal}>{cobroResumen(app.modelo_cobro)}</span></div>
                  <div style={metricRow}><span style={metricLabel}>Cuentas</span><span style={{ ...metricVal, color: 'var(--text-muted)' }}>—</span></div>
                  <div style={metricRow}><span style={metricLabel}>Consumo (mes)</span><span style={{ ...metricVal, color: 'var(--text-muted)' }}>—</span></div>
                </div>
                {app.endpoint && (
                  <a href={app.endpoint} target="_blank" rel="noopener noreferrer" style={endpointLink}>
                    <IconExternalLink size={12} /> {app.endpoint.replace(/^https?:\/\//, '')}
                  </a>
                )}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}

/* ─── styles ─── */
const noticeStyle: CSSProperties = { display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px', border: '1px solid color-mix(in srgb, var(--warning) 25%, transparent)', background: 'color-mix(in srgb, var(--warning) 6%, transparent)', borderRadius: 10, fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 18 }
const kpiGrid: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 28 }
const sectionTitle: CSSProperties = { fontSize: '0.95rem', fontWeight: 700, margin: '0 0 12px' }
const cardGrid: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }
const appCard: CSSProperties = { border: '1px solid var(--border)', borderRadius: 12, padding: 16, background: 'var(--bg-primary)', display: 'flex', flexDirection: 'column' }
const metricRow: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }
const metricLabel: CSSProperties = { fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }
const metricVal: CSSProperties = { fontSize: '0.82rem', fontWeight: 600 }
const endpointLink: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border)', fontSize: '0.74rem', color: 'var(--primary)', textDecoration: 'none', wordBreak: 'break-all' }
const emptyStyle: CSSProperties = { padding: '28px 24px', textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 12, color: 'var(--text-muted)', fontSize: '0.85rem' }
